import React, { useState } from 'react';
import { Invoice } from '@/types';
import { updateInvoiceStatus } from '@/services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/config/firebase';
import { useAuth } from '@/hooks/useAuth';

interface PaymentModalProps {
  invoice: Invoice | null;
  isOpen: boolean;
  onClose: () => void;
  onPaymentSuccess: () => void;
}

// Image compression utility
const compressImage = (file: File, maxWidth: number = 1200, quality: number = 0.8): Promise<File> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!
    const img = new Image()
    
    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img
      if (width > maxWidth) {
        height = (height * maxWidth) / width
        width = maxWidth
      }
      
      canvas.width = width
      canvas.height = height
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height)
      canvas.toBlob((blob) => {
        if (blob) {
          const compressedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          })
          resolve(compressedFile)
        } else {
          resolve(file)
        }
      }, 'image/jpeg', quality)
    }
    
    img.src = URL.createObjectURL(file)
  })
}

export default function PaymentModal({ invoice, isOpen, onClose, onPaymentSuccess }: PaymentModalProps) {
  const { user } = useAuth();
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'bank-transfer'>('cash');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bankTransferImage, setBankTransferImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Bank details from environment variables (secure)
  const bankDetails = {
    bankName: import.meta.env.VITE_BANK_NAME || 'Pro Shipping Solutions Bank',
    accountName: import.meta.env.VITE_ACCOUNT_NAME || 'Pro Shipping Solutions Ltd',
    accountNumber: import.meta.env.VITE_ACCOUNT_NUMBER || '1234567890',
    routingNumber: import.meta.env.VITE_ROUTING_NUMBER || '021000021'
  };

  // Set initial payment method based on existing payment data
  React.useEffect(() => {
    if (invoice?.paymentData?.paymentMethod) {
      setPaymentMethod(invoice.paymentData.paymentMethod);
    }
    if (invoice?.paymentData?.receiptImageUrl) {
      setImagePreview(invoice.paymentData.receiptImageUrl);
    }
  }, [invoice]);

  if (!isOpen || !invoice) {
    return null;
  }

  const handleImageUpload = async (file: File) => {
    console.log('=== FILE UPLOAD HANDLER STARTED ===');
    console.log('File selected for upload:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      isFile: file instanceof File,
      constructor: file.constructor.name
    });

    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.error('Invalid file type:', file.type);
      setError('Please select an image file (PNG, JPG, GIF)');
      return;
    }

    // Validate file size (25MB limit)
    if (file.size > 25 * 1024 * 1024) {
      console.error('File too large:', file.size);
      setError('File size must be less than 25MB');
      return;
    }

    try {
      // Compress image if it's larger than 1MB
      let processedFile = file;
      if (file.size > 1024 * 1024) {
        console.log('Compressing image...');
        processedFile = await compressImage(file);
        console.log('Image compressed:', {
          originalSize: file.size,
          compressedSize: processedFile.size,
          reduction: `${((1 - processedFile.size / file.size) * 100).toFixed(1)}%`
        });
      }

      console.log('File validation passed, setting state...');
      setBankTransferImage(processedFile);
      setError(null); // Clear any previous errors
      
      const reader = new FileReader();
      reader.onload = (e) => {
        console.log('FileReader onload triggered');
        setImagePreview(e.target?.result as string);
        console.log('Image preview generated and set');
      };
      reader.onerror = (e) => {
        console.error('FileReader error:', e);
        setError('Error reading image file');
      };
      reader.readAsDataURL(processedFile);
      console.log('=== FILE UPLOAD HANDLER COMPLETED ===');
    } catch (error) {
      console.error('Error processing image:', error);
      setError('Error processing image. Please try again.');
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('=== FILE INPUT CHANGE EVENT ===');
    console.log('Event target:', e.target);
    console.log('Event target files:', e.target.files);
    console.log('Files length:', e.target.files?.length);
    
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      console.log('File found in input:', file);
      handleImageUpload(file);
    } else {
      console.log('No file selected in input');
      setBankTransferImage(null);
      setImagePreview(null);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      console.log('Payment process started:', { paymentMethod, invoiceId: invoice.id, userId: user?.id });
      
      if (paymentMethod === 'cash') {
        console.log('Processing cash payment...');
        const paymentData = {
          paymentMethod: 'cash',
          uploadedAt: new Date()
        };
        console.log('Saving payment data:', paymentData);
        // For cash payment, set status to pending and save payment data
        await updateInvoiceStatus(invoice.id, 'pending', new Date(), paymentData);
        console.log('Cash payment processed successfully');
        onPaymentSuccess();
        onClose();
      } else if (paymentMethod === 'bank-transfer') {
        console.log('Processing bank transfer payment...');
        // For bank transfer, upload image and set status to pending
        if (!bankTransferImage) {
          setError('Please upload a bank transfer receipt image.');
          setLoading(false);
          return;
        }

        console.log('Bank transfer image validation passed:', {
          fileName: bankTransferImage.name,
          size: bankTransferImage.size,
          type: bankTransferImage.type
        });

        // Upload image to Firebase Storage
        console.log('Starting image upload...', { 
          userId: user?.id, 
          invoiceId: invoice.id, 
          fileName: bankTransferImage.name,
          fileSize: bankTransferImage.size,
          fileType: bankTransferImage.type
        });
        
        setUploadProgress(10);
        
        const imageRef = ref(storage, `payment-receipts/${user?.id}/${Date.now()}-${bankTransferImage.name}`);
        console.log('Image ref created:', imageRef.fullPath);
        
        setUploadProgress(30);
        
        let imageUrl: string;
        try {
          console.log('Starting uploadBytes...');
          const snapshot = await uploadBytes(imageRef, bankTransferImage);
          console.log('Image uploaded successfully:', snapshot);
          
          setUploadProgress(70);
          
          console.log('Getting download URL...');
          imageUrl = await getDownloadURL(snapshot.ref);
          console.log('Image URL obtained:', imageUrl);
          
          setUploadProgress(90);
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          console.error('Upload error details:', {
            code: (uploadError as any)?.code,
            message: (uploadError as any)?.message,
            serverResponse: (uploadError as any)?.serverResponse
          });
          setError(`Image upload failed: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`);
          setLoading(false);
          return;
        }

        // Update invoice with pending status and image URL
        console.log('Updating invoice status with payment data:', {
          invoiceId: invoice.id,
          status: 'pending',
          paymentData: {
            paymentMethod: 'bank-transfer',
            receiptImageUrl: imageUrl,
            uploadedAt: new Date()
          }
        });
        
        try {
          await updateInvoiceStatus(invoice.id, 'pending', new Date(), {
            paymentMethod: 'bank-transfer',
            receiptImageUrl: imageUrl,
            uploadedAt: new Date()
          });
          console.log('Invoice status updated successfully');
          setUploadProgress(100);
        } catch (updateError) {
          console.error('Invoice status update failed:', updateError);
          setError(`Invoice update failed: ${updateError instanceof Error ? updateError.message : 'Unknown error'}`);
          setLoading(false);
          return;
        }

        onPaymentSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setError('Payment processing failed. Please try again.');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Payment Details</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-gray-600 mt-2">
            Invoice #{invoice.invoiceNumber} - {formatCurrency(invoice.total)}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Payment Method Selection */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-3">Payment Method</h3>
            <div className="space-y-3">
              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cash"
                  checked={paymentMethod === 'cash'}
                  onChange={(e) => setPaymentMethod(e.target.value as 'cash' | 'bank-transfer')}
                  className="mr-3"
                />
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                  <span>Pay with Cash</span>
                </div>
              </label>
              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="bank-transfer"
                  checked={paymentMethod === 'bank-transfer'}
                  onChange={(e) => setPaymentMethod(e.target.value as 'cash' | 'bank-transfer')}
                  className="mr-3"
                />
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  <span>Bank Transfer</span>
                </div>
              </label>
            </div>
          </div>

          {/* Payment Details */}
          {paymentMethod === 'cash' && (
            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-blue-800 font-medium">Pay with Cash</span>
                </div>
                <p className="text-blue-700 text-sm mt-2">
                  When you select cash payment, your invoice will be marked as pending. 
                  Please bring cash payment to our office to complete the transaction.
                </p>
              </div>
            </div>
          )}

          {paymentMethod === 'bank-transfer' && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">Bank Transfer Details</h3>
              
              {/* Bank Account Information */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center mb-3">
                  <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  <span className="text-green-800 font-medium">Send Money To:</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bank Name:</span>
                    <span className="font-medium">{bankDetails.bankName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Account Name:</span>
                    <span className="font-medium">{bankDetails.accountName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Account Number:</span>
                    <span className="font-medium font-mono">{bankDetails.accountNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Routing Number:</span>
                    <span className="font-medium font-mono">{bankDetails.routingNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-bold text-green-600">{formatCurrency(invoice.total)}</span>
                  </div>
                </div>
              </div>

              {/* Upload Receipt Section */}
              <div className="space-y-4">
                <div className={`border-2 border-dashed rounded-lg p-6 transition-colors ${
                  bankTransferImage ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-gray-400'
                }`}>
                  <div className="text-center">
                    <svg className={`mx-auto h-12 w-12 ${bankTransferImage ? 'text-green-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <div className="mt-4">
                      <label htmlFor="receipt-upload" className="cursor-pointer hover:text-blue-600 transition-colors">
                        <span className="mt-2 block text-sm font-medium text-gray-900">
                          {bankTransferImage ? 'File Selected: ' + bankTransferImage.name : 'Upload Transfer Confirmation'}
                        </span>
                        <span className="mt-1 block text-xs text-gray-500">
                          {bankTransferImage ? `Selected: ${bankTransferImage.name} (${(bankTransferImage.size / 1024 / 1024).toFixed(1)}MB)` : 'PNG, JPG, GIF up to 25MB (auto-compressed)'}
                        </span>
                      </label>
                      <input
                        id="receipt-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleFileInputChange}
                        onClick={() => {
                          console.log('File input clicked - clearing previous selection');
                        }}
                        className="sr-only"
                        required
                      />
                    </div>
                  </div>
                </div>
                
                {imagePreview && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Preview:</h4>
                    <img 
                      src={imagePreview} 
                      alt="Transfer confirmation preview" 
                      className="max-w-full h-32 object-contain border rounded-lg"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
        </div>

        {/* Action Buttons - Fixed at bottom */}
        <div className="p-6 pt-4 border-t border-gray-200">
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handlePayment}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {uploadProgress > 0 ? `Processing... ${uploadProgress}%` : 'Processing...'}
                </div>
              ) : (
                paymentMethod === 'cash' 
                  ? `${invoice.paymentData ? 'Update' : 'Mark as'} Cash Payment` 
                  : `${invoice.paymentData ? 'Update' : 'Submit'} Transfer Confirmation`
              )}
            </button>
          </div>

          {/* Security Notice */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              🔒 Your payment information is secure and encrypted
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 